import { createPortal } from 'react-dom';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { useRegisterSlotCanvas } from '../../slot-canvas/useRegisterSlotCanvas';
import { SlotCanvasLayout } from '../../slot-canvas/SlotCanvasLayout';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import { TaskDetailBody } from '@/components/tasks/detail/TaskDetailBody';
import { TaskDetailFooter } from '@/components/tasks/detail/TaskDetailFooter';
import { useOpenTaskInChat } from '@/components/tasks/useOpenTaskInChat';

/** The canvas is one slot per task, so two cards for the same task share one. */
export const taskSlotId = (taskId: string) => `task_detail-${taskId}`;

/** Narrower than a report: the panel is a property list, not a document. */
const PREFERRED_WIDTH_PX = 480;

export interface TaskDetailSlotProps {
  taskId: string;
  /** The card's title, which names the slot before the task itself is fetched. */
  title: string;
}

/**
 * Registers one task's details as a canvas slot, opened by clicking its card.
 *
 * Registration is not opening: cards are receipts that stay in the transcript,
 * so a conversation reloaded with five of them registers five slots and opens
 * none of them.
 */
export const TaskDetailSlot = ({ taskId, title }: TaskDetailSlotProps) => {
  const { workflowExecutionId } = useChatMetadata();
  const bodyMount = useSlotCanvasStore((state) => state.bodyMountElement);
  const openInChat = useOpenTaskInChat();

  const { isOpen } = useRegisterSlotCanvas({
    id: taskSlotId(taskId),
    experience: 'task_detail',
    title,
    autoOpen: false,
    hasInStreamCard: true,
    preferredWidthPx: PREFERRED_WIDTH_PX,
  });

  if (!isOpen || !bodyMount) {
    return null;
  }

  return createPortal(
    <SlotCanvasLayout
      title={title}
      subtitle="Task"
      // The panel owns its own scroll and pins its own footer, so the canvas
      // body must not scroll it a second time or pad it a second time.
      bodyClassName="flex flex-col overflow-hidden"
    >
      <TaskDetailBody
        taskId={taskId}
        currentExecutionId={workflowExecutionId}
        footer={(task, save) => (
          <TaskDetailFooter
            task={task}
            ctaLabel="Open in new chat"
            onMarkDone={() => save({ status: 'COMPLETED' })}
            onOpenInChat={() => openInChat(task, 'new_chat')}
          />
        )}
      />
    </SlotCanvasLayout>,
    bodyMount,
  );
};
